import React, { useEffect, useState } from "react";
import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  useTheme,
  IconButton,
  TextField,
  Button,
  Box,
  TableContainer,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import VantageListHead from "../../../component/ListHead/SignalList";
import { sendWebSocketMessage } from "../../../hooks/useWebSocket";
import {
  Tablecell,
  TABLE_HEAD_SIGNALS as TABLE_HEAD,
  applySortFilter,
  getComparator,
  hashCurrencyEnumString,
  hashInvestmentReverse,
  hashExchangeEnumReverse,
  inputChecker,
  hashInvestment,
  investArr,
  normalizeTickerForComparison,
} from "../../../component/Methods";
import "../../../Style/Signals.css";
import { useDispatch} from "react-redux";
import { toast } from "react-toastify";
import SaveSignals from "../Popup/SaveSignals";
import ClearConfirmationPopup from "../Popup/ClearConfirmationPopup";
import {
  addActiveRequests,
  updateLoading,
  updateLoadingMsg,
} from "../../../Redux/Reducers/uiSlice";

const SignalModifiedList = ({
  pastedArray,
  setpasted,
  strategyValObj,
  setVerifyEnabled,
  setCopiedData,
  setHandleVerify,
  setNumberOfInvalidObjects,
  setEmpty,
  setError,
  setSaveChangeHandler,
  onDataChange,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("value");
  const [saveChange, setSaveChange] = useState(false);
  const [clearConfirmationOpen, setClearConfirmationOpen] = useState(false);
  const [editCell, setEditCell] = useState({ index: null, field: null });

 

  useEffect(() => {
    const updatedData = applySortFilter(data, (a, b) => {
      if (a.invalid && !b.invalid) return -1;
      if (!a.invalid && b.invalid) return 1;
      return getComparator(order, orderBy)(a, b);
    });

    // Revalidate the data after sorting
    const validatedData = updatedData.map((item) => {
      const testName = inputChecker(item.name);
      return {
        ...item,
        invalid: !testName.isValid,
      };
    });

    // Only update if there are actual changes
    if (JSON.stringify(validatedData) !== JSON.stringify(data)) {
      setData(validatedData);
      setCopiedData(validatedData);
    }
  }, [order, orderBy]);

  useEffect(() => {
    if (pastedArray) {
      setData(pastedArray);
      setCopiedData(pastedArray);
    }
  }, [pastedArray]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleVerify = () => {
    const updatedData = data?.map((item) => {
      const { ticker } = inputChecker(item?.name) ?? {};
      const { symbol, currency, investment, country } = ticker ?? {};
      const curr = hashCurrencyEnumString(currency) ?? 7;
      const invest = hashInvestmentReverse(investment) ?? 4;
      const exchange = hashExchangeEnumReverse(country) ?? 4;

      return {
        symbol,
        currency: curr,
        investment: invest,
        exchange,
      };
    });

    sendWebSocketMessage(
      JSON.stringify({
        action: "market_data",
        data: {
          threshold: 2592000,
          request: true,
          fields: [9, 10],
          tickers: updatedData,
        },
      })
    );
    dispatch(addActiveRequests("market_data"));
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Fetching Market Data"));
    toast.success("Verification Initiated");
  };

  const hasInvalidObject = data?.some((obj) => obj && obj?.invalid);
  const countInvalidObjects = (data) => {
    return data?.filter((obj) => obj && obj?.invalid).length;
  };
 

  useEffect(() => {
    setNumberOfInvalidObjects(countInvalidObjects(data));
   
  }, [
    data,
    setNumberOfInvalidObjects,
    
  ]);

  useEffect(() => {
    // setVerifyEnabled(data.length > 0 && !hasInvalidObject);
    setHandleVerify(() => handleVerify);
    setSaveChangeHandler(() => setSaveChange);
  }, [
    data,
    hasInvalidObject,
    setVerifyEnabled,
    setEmpty,
    setHandleVerify,
    setSaveChangeHandler,
  ]);

  // Notify parent component when data changes
  useEffect(() => {
    if (onDataChange) {
      onDataChange(data);
    }
  }, [data, onDataChange]);
  const handleKeyDown = (e) => {
    const disallowedKeys = ["e", "E", "+"];
    if (
      disallowedKeys.includes(e.key) &&
      !(e.key === "-" && e.target.value.length === 0)
    ) {
      e.preventDefault();
    }
  };

  const checkForInvalidRows = () => {
    const hasInvalidRows = data.some(
      (row) =>
        row.name === "" ||
        Number(row.value) === 0 ||
        row.value === "" ||
        row.invalid
    );
    setEmpty(hasInvalidRows);
    setVerifyEnabled(!hasInvalidRows);
  };

  const handleDeleteRow = (index) => {
    const deletedRow = data[index];
    const updatedData = data.filter((_, i) => i !== index);
    setData(updatedData);
    setCopiedData(updatedData);
    
    // Clear any existing error messages when deleting a row
    setError("");
    
    if (
      deletedRow.name === "" ||
      deletedRow.value === 0 ||
      deletedRow.value === "" ||
      deletedRow.invalid
    ) {
      const hasInvalidRows = updatedData.some(
        (row) =>
          row.name === "" || row.value === 0 || row.value === "" || row.invalid
      );

      if (hasInvalidRows) {
        setEmpty(true);
        setVerifyEnabled(false);
      } else {
        setEmpty(false);
        setVerifyEnabled(true);
      }
    } else {
      checkForInvalidRows();
    }
  };

  const handleAddRow = () => {
    const newRow = {
      name: "",
      value: 0,
      investment: 4, // Default to Equity
    };
    setData([newRow, ...data]);
    setEmpty(true);
    setVerifyEnabled(false);
  };

  const handleEditRow = (index, field, value) => {
    const updatedData = [...data];
    updatedData[index] = { ...updatedData[index], [field]: value };

    const editedEntry = updatedData[index];
    const editedName = editedEntry.name;
    const editedValue = editedEntry.value;
    const testName = inputChecker(editedName);
    if (testName.isValid) {
      delete editedEntry.invalid;
    } else {
      editedEntry.invalid = true;
    }
    
    // Check for duplicate names using normalized ticker comparison (TSLA.B = TSLA/B)
    const normalizedEditedName = normalizeTickerForComparison(editedName);
    const isDuplicateName = updatedData.some(
      (row, i) => {
        const normalizedRowName = normalizeTickerForComparison(row?.name);
        return normalizedRowName && normalizedRowName === normalizedEditedName && i !== index;
      }
    );

    // Check for empty or zero values
    const hasEmptyValue = editedValue === "" || Number(editedValue) === 0;

    // Set error messages based on validation
    if (isDuplicateName) {
      setError("Name already exists");
    } else if (hasEmptyValue) {
      setError("Weight cannot be empty or zero");
    } else {
      setError("");
    }

    const hasInvalidRows = updatedData.some(
      (row) =>
        row.name === "" ||
        Number(row.value) === 0 ||
        row.value === "" ||
        row.invalid
    );

    const hasInvalidTickers = updatedData.some((row) => row.invalid);

    if (!isDuplicateName && !hasEmptyValue && hasInvalidRows) {
      setEmpty(true);
      setVerifyEnabled(false);
    } else if (!isDuplicateName && !hasEmptyValue && !hasInvalidTickers) {
      setEmpty(false);
      setVerifyEnabled(true);
    } else {
      setEmpty(true);
      setVerifyEnabled(false);
    }

    // Immediately update all states
    setData(updatedData);
    setCopiedData(updatedData);
    setpasted(updatedData);
  };

  const handleChangeEvent = (index, e) => {
    const newValue = e.target.value;
    const regex = /^-?\d*\.?\d{0,4}$/;

    if (regex.test(newValue)) {
      handleEditRow(index, "value", newValue);
    } else if (newValue === "" || newValue === "-") {
      handleEditRow(index, "value", newValue);
    }
  };
  return (
    <>
      <Box
        sx={{
          borderRadius: "4px",
          marginTop: "10px",
          width: "100%",
          height: "100%",
          marginRight: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Hidden trigger buttons for parent component to access */}
        <Button
          data-testid="clear-confirmation-trigger"
          onClick={() => setClearConfirmationOpen(true)}
          style={{ display: "none" }}
        >
          Clear
        </Button>
        <Button
          data-testid="add-signal-trigger"
          onClick={handleAddRow}
          style={{ display: "none" }}
        >
          Add Signal
        </Button>

        <TableContainer 
          sx={{ 
            flex: 1,
            overflowX: "hidden",
            overflowY: "auto",
          }}
        >
          <Table
            size="small"
            stickyHeader
            aria-label="a dense table"
          >
            <VantageListHead
              order={order}
              orderBy={orderBy}
              headLabel={TABLE_HEAD}
              onRequestSort={handleRequestSort}
              noStatus={true}
              customizeFont={12}
            />

            <TableBody>
              {data?.map((row, index) => (
                <TableRow
                  key={row?.key}
                  sx={{
                    overflow: "auto",
                    color: row?.invalid ? "white" : "black",
                    background: row?.invalid
                      ? theme.palette.red.shortPositions
                      : "",
                    "&:hover": {
                      background: row?.invalid
                        ? theme.palette.red.shortPositions
                        : theme.palette.grey[200],

                      cursor: "pointer",
                    },
                    "& > .MuiTableCell-root": {
                      paddingTop: "1px",
                      paddingBottom: "0px !important",
                      lineHeight: "1.2",
                      "& .MuiInputBase-input": {
                        padding: "0px 0px !important",
                      },
                      "& .MuiIconButton-root": {
                        padding: "0px",
                      },
                    },
                  }}
                >
                  <TableCell
                    align="left"
                    sx={{ borderRight: "none", cursor: "pointer" }}
                    onClick={() => setEditCell({ index, field: "name" })}
                  >
                    {editCell.index === index && editCell.field === "name" ? (
                      <TextField
                        value={row.name}
                        onChange={(e) =>
                          handleEditRow(index, "name", e.target.value)
                        }
                        onBlur={() => setEditCell({ index: null, field: null })}
                        size="small"
                        variant="standard"
                        autoFocus
                        InputProps={{
                          disableUnderline: true,
                          style: {
                            fontSize: "12px",
                            fontWeight: "500",
                            paddingTop: "0px",
                            paddingBottom: "0px",
                            width: "80px",
                          },
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                      
                          paddingTop: "0px",
                          paddingBottom: "0px",
                        }}
                      >
                        {row?.name?.toUpperCase() || ""}
                      </Box>
                    )}
                  </TableCell>

                  <TableCell
                    align="left"
                    sx={{ borderLeft: "none", cursor: "pointer" }}
                    onClick={() => setEditCell({ index, field: "value" })}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "left",
                      }}
                    >
                      {editCell.index === index &&
                      editCell.field === "value" ? (
                        <TextField
                          value={row?.value}
                          onChange={(e) => handleChangeEvent(index, e)}
                          onBlur={() =>
                            setEditCell({ index: null, field: null })
                          }
                          onKeyDown={handleKeyDown}
                          size="small"
                          variant="standard"
                          autoFocus
                          InputProps={{
                            disableUnderline: true,
                            style: {
                              fontSize: "12px",
                              fontWeight: "500",
                              width: "65px",
                              marginRight: "0.5ch",
                              paddingTop: "0px",
                              paddingBottom: "0px",
                            },
                          }}
                          inputProps={{
                            type: "number",
                            step: "any",
                            style: {
                              textAlign: "right",
                              paddingTop: "0px",
                              paddingBottom: "0px",
                              MozAppearance: "textfield",
                              WebkitAppearance: "none",
                              "&::WebkitOuterSpinButton": {
                                WebkitAppearance: "none",
                                margin: 0,
                              },
                              "&::WebkitInnerSpinButton": {
                                WebkitAppearance: "none",
                                margin: 0,
                              },
                            },
                          }}
                        />
                      ) : (
                        <>
                          {row?.value}
                          <Box
                            sx={{
                              fontSize: "12px",
                              fontWeight: "500",
                              width: "10px",
                              height: "10px",
                              mt: "2px",
                            }}
                          >
                            %
                          </Box>
                        </>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell
                    align="left"
                    sx={{
                      borderRight: "none",
                      borderLeft: "none",
                      height: "21.5px",
                    }}
                  >
                    {row?.value >= 0 ? "L" : "S"}
                  </TableCell>

                  <TableCell
                    align="left"
                    className="lastTableCell"
                    sx={{
                      borderLeft: "none",
                      borderRight: "none",
                      height: "21.5px",
                      display: "flex",
                      justifyContent: "space-between",
                      "&:hover": {
                        ".deleteIcon": {
                          visibility: "visible",
                        },
                      },
                    }}
                  >
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <Select
                        value={hashInvestment(row?.investment) || "Equity"}
                        onChange={(e) => {
                          const investmentValue = hashInvestmentReverse(e.target.value);
                          handleEditRow(index, "investment", investmentValue);
                        }}
                        variant="standard"
                        disableUnderline
                        sx={{
                          fontSize: "12px",
                          "& .MuiSelect-select": {
                            padding: "0px",
                            paddingRight: "0px !important",
                            background: "transparent",
                            "&:focus": {
                              background: "transparent",
                            },
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "&:before": {
                            borderBottom: "none",
                          },
                          "&:after": {
                            borderBottom: "none",
                          },
                          "&:hover:not(.Mui-disabled):before": {
                            borderBottom: "none",
                          },
                          "& .MuiSelect-icon": {
                            display: "none",
                          },
                        }}
                      >
                        {investArr.map((type) => (
                          <MenuItem key={type} value={type} sx={{ fontSize: "12px" }}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Box
                      component="span"
                      sx={{
                        fontSize: "9px",
                        fontWeight: "500",
                        visibility: "hidden",
                      }}
                      className="deleteIcon"
                    >
                      <IconButton
                        onClick={() => handleDeleteRow(index)}
                        aria-label="delete"
                        sx={{
                          height: "15px",
                          width: "15px",
                        }}
                      >
                        <DeleteOutlineIcon
                          sx={{ height: "15px", width: "15px" }}
                        />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <ClearConfirmationPopup
        open={clearConfirmationOpen}
        onClose={() => setClearConfirmationOpen(false)}
        title="Confirm Action"
        onConfirm={() => {
          // Clear both the parent's pasted array and local data
          setpasted([]);
          setData([]);
          setCopiedData([]);
          setClearConfirmationOpen(false);
          setVerifyEnabled(false);
          setEmpty(true);
        }}
      />
      <SaveSignals
        saveChange={saveChange}
        setSaveChange={setSaveChange}
        data={data}
        title="Update Signals"
        setpasted={setpasted}
        strategyValObj={strategyValObj}
      />
    </>
  );
};

export default SignalModifiedList;
